import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import toast from "react-hot-toast";
import {
  changeCurrentUserPassword,
  updateCurrentUserProfile,
} from "../services/api";
import { searcherApi } from "../services/searcherApi";
import { useAuth } from "../contexts/AuthContext";
import { PasswordRequirementsChecklist } from "./PasswordRequirementsChecklist";
import { validatePassword, describeMissingRequirements } from "../utils/passwordPolicy";

type UserSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const UserSettingsDialog: React.FC<UserSettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const { user, refreshUser } = useAuth();
  const [firstname, setFirstname] = React.useState("");
  const [lastname, setLastname] = React.useState("");
  const [changePassword, setChangePassword] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [syncConfirmOpen, setSyncConfirmOpen] = React.useState(false);
  const [syncLoading, setSyncLoading] = React.useState(false);

  const isAdminOrMaintainer =
    user?.role === "admin" || user?.role === "mantainer";

  React.useEffect(() => {
    if (!open) return;
    setFirstname(user?.firstname || "");
    setLastname(user?.lastname || "");
    setChangePassword(false);
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
  }, [open, user]);

  const handleDialogClose = () => {
    if (loading || syncLoading) return;
    onClose();
  };

  const handleSaveSettings = async () => {
    if (changePassword) {
      if (!currentPassword || !newPassword || !newPasswordConfirm) {
        toast.error("Compila tutti i campi password");
        return;
      }
      if (newPassword !== newPasswordConfirm) {
        toast.error("Le nuove password non coincidono");
        return;
      }
      if (!validatePassword(newPassword).valid) {
        toast.error(describeMissingRequirements(newPassword));
        return;
      }
    }

    setLoading(true);
    try {
      await updateCurrentUserProfile({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
      });

      if (changePassword) {
        await changeCurrentUserPassword({
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirm: newPasswordConfirm,
        });
      }

      await refreshUser();
      toast.success("Profilo aggiornato con successo");
      handleDialogClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail || "Errore nell'aggiornamento del profilo"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSyncConfirm = async () => {
    setSyncConfirmOpen(false);
    setSyncLoading(true);
    const toastId = toast.loading("Sincronizzazione indice in corso…");
    try {
      const res = await searcherApi.syncAll();
      toast.success(`Indice ricostruito: ${res.synced} elementi sincronizzati`, {
        id: toastId,
        duration: 6000,
      });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail || "Errore durante la sincronizzazione",
        { id: toastId }
      );
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Impostazioni Profilo</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              fullWidth
            />
            <TextField
              label="Cognome"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={changePassword}
                  onChange={(e) => setChangePassword(e.target.checked)}
                />
              }
              label="Cambia password"
            />
            {changePassword && (
              <>
                <TextField
                  label="Password attuale"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Nuova password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                />
                {newPassword.length > 0 && (
                  <PasswordRequirementsChecklist password={newPassword} />
                )}
                <TextField
                  label="Conferma nuova password"
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  fullWidth
                />
              </>
            )}

            {isAdminOrMaintainer && (
              <>
                <Divider sx={{ mt: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Amministrazione
                </Typography>
                <Alert severity="info" sx={{ fontSize: "0.82rem" }}>
                  Ricostruisce l'indice di ricerca leggendo tutti gli elementi
                  del catalogo. Necessario dopo un'importazione fallita o per
                  sincronizzare manualmente il motore di ricerca.
                </Alert>
                <Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={
                      syncLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SyncIcon />
                      )
                    }
                    disabled={syncLoading || loading}
                    onClick={() => setSyncConfirmOpen(true)}
                  >
                    {syncLoading
                      ? "Sincronizzazione…"
                      : "Ricostruisci indice di ricerca"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box>
            <Button onClick={handleDialogClose} disabled={loading || syncLoading}>
              Annulla
            </Button>
            <Button
              sx={{ ml: 2 }}
              onClick={handleSaveSettings}
              variant="contained"
              disabled={
                loading ||
                syncLoading ||
                (changePassword &&
                  (!validatePassword(newPassword).valid ||
                    newPassword !== newPasswordConfirm))
              }
            >
              {loading ? <CircularProgress size={24} /> : "Salva"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={syncConfirmOpen}
        onClose={() => setSyncConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Ricostruisci indice di ricerca?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa operazione rigenera l'intero indice vettoriale e FTS leggendo
            tutti gli elementi del catalogo. Può richiedere diversi minuti in
            base alla dimensione del catalogo.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Le ricerche in corso non vengono interrotte ma potrebbero restituire
            risultati parziali durante la sincronizzazione.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmOpen(false)}>Annulla</Button>
          <Button
            onClick={handleSyncConfirm}
            variant="contained"
            color="warning"
            autoFocus
          >
            Avvia sincronizzazione
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
